/**
 * ============================================================================
 * NOTIFICATIONS PAGE
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { useNotificationStore } from '../store';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import NotificationItem from '../components/notification/NotificationItem';
import NotificationSettings from '../components/notification/NotificationSettings';
import EmptyState from '../components/common/EmptyState';
import Spinner from '../components/common/Spinner';
import Tabs from '../components/common/Tabs';
import '../styles/landing.css';
import '../styles/animations.css';

/**
 * Notifications Page Component
 * @returns {JSX.Element}
 */
const Notifications = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } = useNotificationStore();
  const { markAsRead, markAllAsRead, deleteNotification, loadNotifications } = useNotifications();
  const { language } = useI18n();

  const [activeTab, setActiveTab] = useState('notifications');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'unread') return !(notification.isRead || notification.read);
    if (filter === 'read') return notification.isRead || notification.read;
    return true;
  });

  const handleNotificationClick = (notification) => {
    if (!(notification.isRead || notification.read)) {
      markAsRead(notification.id);
    }

    if (notification.transactionId) {
      navigate(`/transactions/${notification.transactionId}`);
    } else if (notification.disputeId) {
      navigate(`/disputes/${notification.disputeId}`);
    }
  };

  const tabs = [
    {
      id: 'notifications',
      label: language === 'fr' ? 'Notifications' : 'Notifications',
      icon: '🔔',
      count: unreadCount,
    },
    {
      id: 'settings',
      label: language === 'fr' ? 'Paramètres' : 'Settings',
      icon: '⚙️',
    },
  ];

  const filterTabs = [
    { id: 'all', label: language === 'fr' ? 'Toutes' : 'All' },
    { id: 'unread', label: language === 'fr' ? 'Non lues' : 'Unread', count: unreadCount },
    { id: 'read', label: language === 'fr' ? 'Lues' : 'Read' },
  ];

  return (
    <UserDashboardLayout>
      <div className="dashboard-wt dashboard-wt-section animate-fadeInUp">
        {/* Header */}
        <div className="dashboard-wt-hero-inner mb-8">
          <h1 className="dashboard-wt-hero-title">
            🔔 {language === 'fr' ? 'Notifications' : 'Notifications'}
          </h1>
          <p className="dashboard-wt-hero-sub">
            {language === 'fr'
              ? 'Gérez vos notifications et préférences'
              : 'Manage your notifications and preferences'}
          </p>
        </div>

        {/* Main Tabs: Notifications / Settings */}
        <div className="dashboard-wt-card mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="dashboard-wt-card-content p-4">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              variant="pills"
            />
          </div>
        </div>

        {activeTab === 'notifications' ? (
          <div className="space-y-6">
            {/* Filter + Mark all read */}
            <div className="dashboard-wt-card animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <div className="dashboard-wt-card-content p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Tabs
                    tabs={filterTabs}
                    activeTab={filter}
                    onTabChange={setFilter}
                    variant="pills-green"
                  />
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllAsRead}
                      className="dashboard-wt-btn-secondary whitespace-nowrap"
                    >
                      ✓ {language === 'fr' ? 'Tout marquer comme lu' : 'Mark all as read'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* List or empty or loading */}
            {loading ? (
              <div className="dashboard-wt-card p-12">
                <div className="flex items-center justify-center">
                  <Spinner size="lg" />
                </div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="dashboard-wt-card p-12 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                <EmptyState
                  icon="🔔"
                  title={
                    filter === 'unread'
                      ? language === 'fr'
                        ? 'Aucune notification non lue'
                        : 'No unread notifications'
                      : language === 'fr'
                      ? 'Aucune notification'
                      : 'No notifications'
                  }
                  description={
                    filter === 'unread'
                      ? language === 'fr'
                        ? "Vous êtes à jour!"
                        : "You're all caught up!"
                      : language === 'fr'
                      ? 'Vous verrez vos notifications ici'
                      : "You'll see your notifications here"
                  }
                />
              </div>
            ) : (
              <div className="space-y-3 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                {filteredNotifications.map((notification, index) => (
                  <NotificationItem
                    key={notification.id || index}
                    notification={notification}
                    onClick={handleNotificationClick}
                    onDelete={deleteNotification}
                    onMarkRead={markAsRead}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="dashboard-wt-card animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="dashboard-wt-card-content p-6">
              <NotificationSettings />
            </div>
          </div>
        )}
      </div>
    </UserDashboardLayout>
  );
};

export default Notifications;
